import React, { useEffect } from "react";
import { routes } from "../../../../constants/routes";
import { Alert, Box, Tab, Tabs, Typography } from "@mui/material";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../../common/components";
import PlanDetails from "./crm-plan-details/crm-plan-details";
import "./create-new-plan.scss";
import PriceDetails from "./crm-price-details/crm-price-detials";
import CRMBenefits from "./crm-benefits/crm-benefits";
import ViewCRMPriceDetails from "../crm-paid-plans/view-crm-price-details.tsx/view-crm-price-details";
import { Form, Formik } from "formik";
import {
  getNoCostPlans,
  getTrailPlans,
  initialValues,
  validationSchema,
} from "../crm-plans-utils";
import { createCRMPlan } from "../../../../api/crm-plans";
import { AlertMessage } from "../../tenant-details/tenant-details-types";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { getSubscriptionPlans } from "../../../../api/tenets";
import { Link } from "react-router-dom";

const breadcrumbLink = [
  {
    label: "CRM Plans",
    link: "#",
    handleClick: () => {},
  },
  {
    label: "Create New Plan",
    link: routes.CREATE_CRM_PLAN,
    handleClick: () => {},
  },
];

const CreateNewPlan = () => {
  const [indexValue, setIndexValue] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [noCostPlans, setNoCostPlans] = React.useState<any>([]);
  const [existingPlans, setExistingPlans] = React.useState<any>([]);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
  });

  const planCategory = {
    0: "paid",
    1: "trial",
    2: "no cost",
  };

  const getSubscriptionValues = async () => {
    try {
      setIsLoading(true);
      const response = await getSubscriptionPlans();
      const resPlans = response?.data?.data || [];

      const { plans } = getNoCostPlans(resPlans);

      setExistingPlans(resPlans);
      setNoCostPlans(plans);
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Some thing went wrong!",
        type: "error",
      });
      console.error(e);
    }
  };

  const onSubmit = async (values: any, { resetForm }: any) => {
    try {
      setIsLoading(true);

      const supprtlevel = values?.supportLevel
        ?.filter((item: any) => item?.checked)
        ?.map((item: any) => item?.name?.toLowerCase());

      const payload = {
        ...values,
        noOfUsers: Number(values?.noOfUsers ?? ""),
        automationRulesLimit: Number(values?.automationRulesLimit ?? ""),
        noOfLeads: Number(values?.noOfLeads ?? ""),
        noOfContacts: Number(values?.noOfContacts ?? ""),
        apiCallsPerMonth: Number(values?.apiCallsPerMonth ?? ""),
        supportLevel: supprtlevel?.length ? supprtlevel : null,
        storageLimit: values?.storageLimit?.value || null,
        reportingLevel: values?.reportingLevel === "Yes",
        planCategory: planCategory[indexValue as keyof typeof planCategory],
      };
      if (payload?.planType) {
        payload.planType = payload?.planType?.value;
      } else {
        delete payload?.planType;
      }

      const res = await createCRMPlan(payload);
      setAlertMessage({
        open: true,
        message:
          res?.data?.data?.message || "New plan has been created successfully.",
        type: "success",
      });
      resetForm();
      setIsLoading(false);
    } catch (e: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          e?.response?.data?.message ||
          e?.response?.data?.error ||
          e?.message ||
          "Some thing went wrong!",
        type: "error",
      });
      console.error(e);
    }
  };

  useEffect(() => {
    getSubscriptionValues();
  }, []);

  const showLoading = React.useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box className="create-new-plan-wrapper">
      <Box>
        <BreadcrumbComponent breadcrumbLink={breadcrumbLink} />
      </Box>
      <Box className="create-new-plan-content-wrapper">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnBlur
          onSubmit={onSubmit}
        >
          {({ resetForm, dirty, isValid }) => (
            <Form>
              <Box className="crm-tab-edit">
                <Box className="tab-section">
                  <Tabs
                    value={indexValue}
                    onChange={(
                      event: React.SyntheticEvent,
                      newValue: number
                    ) => {
                      const defaultIntialValues: any = { ...initialValues };
                      if (newValue === 1) {
                        defaultIntialValues.planCategory = "trial";
                        defaultIntialValues.monthlyPrice = 0;
                        defaultIntialValues.annualPrice = 0;
                        defaultIntialValues.planName = "Trial";
                      } else if (newValue === 2) {
                        defaultIntialValues.planCategory = "noCost";
                        defaultIntialValues.planName = "No Cost";
                        defaultIntialValues.monthlyPrice = 0;
                        defaultIntialValues.annualPrice = 0;
                      }
                      resetForm({ values: { ...defaultIntialValues } });
                      setIndexValue(newValue);
                    }}
                    allowScrollButtonsMobile
                    scrollButtons
                    variant="scrollable"
                    sx={{
                      "& .MuiTabs-scrollButtons": {
                        width: 32, // or your preferred size
                      },
                    }}
                  >
                    <Tab
                      label={
                        <Box className="tab-label">
                          <Typography className="tab-label-text">
                            Paid Plan
                          </Typography>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box className="tab-label">
                          <Typography className="tab-label-text">
                            Trial Plan
                          </Typography>
                        </Box>
                      }
                    />
                    <Tab
                      label={
                        <Box className="tab-label">
                          <Typography className="tab-label-text">
                            No Cost Plan
                          </Typography>
                        </Box>
                      }
                    />
                  </Tabs>
                </Box>
              </Box>
              {indexValue === 2 && noCostPlans?.length ? (
                <div className="create-no-plans">
                  <Alert severity="info">
                    A "No Cost" plan already exists. Please go to the{" "}
                    <strong>
                      {" "}
                      <Link
                        to={routes.CRM_NO_COST_PLAN}
                        style={{
                          textDecoration: "underline",
                          color: "#1976d2",
                        }}
                      >
                        No Cost Plans
                      </Link>{" "}
                    </strong>
                    tab to edit the details.
                  </Alert>
                </div>
              ) : (
                <Box>
                  <Box className="create-plan-price-wrapper" gap={2}>
                    <Box className="create-plan-details-wrapper">
                      <PlanDetails
                        activeTab={indexValue}
                        mode="create"
                        existingPlans={existingPlans}
                      />
                    </Box>
                    <Box className="create-plan-price-wrapper">
                      {indexValue === 0 ? (
                        <PriceDetails mode="create" />
                      ) : (
                        <ViewCRMPriceDetails
                          setIsEditPrice={() => {}}
                          from="create"
                        />
                      )}
                    </Box>
                  </Box>
                  <Box className="create-crm-benefits-wrapprer">
                    <CRMBenefits />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      paddingBottom: "20px",
                      marginTop: "20px",
                    }}
                  >
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => {
                        resetForm();
                      }}
                    >
                      Reset Changes
                    </button>
                    <button
                      type="submit"
                      disabled={!dirty || !isValid}
                      className="cancel-button next-button-wrapper"
                    >
                      Save Plan
                    </button>
                  </Box>
                </Box>
              )}
            </Form>
          )}
        </Formik>
      </Box>
      {alertMessage?.open && (
        <AlertComponent
          message={alertMessage?.message}
          type={alertMessage?.type || "error"}
          open={alertMessage?.open}
          onClose={() => {
            setAlertMessage({ open: false });
          }}
        />
      )}
    </Box>
  );
};

export default CreateNewPlan;
